import { useState } from 'react';
import { useUpdateEffect } from 'react-use';
import {
  Alert,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

import { LocalizationTypography } from './localization';

import { FEATURE_FLAGS, TRACKING_BUTTON } from '~common';
import { MainButton, PhoneInput } from '~components/atoms';
import { PhoneField } from '~components/atoms/custom/PhoneField';
import { useNavigateInAppWithDelay } from '~hooks/useNavigateInAppWithDelay';
import {
  useEventTrackingContext,
  useMainContext,
  useVenueCountryContext,
} from '~providers';
import { checkEnabledFeatureFlag } from '~utils';

export const StepOne = () => {
  // Flag
  const isEnabledPhoneNumberRevamp = checkEnabledFeatureFlag(
    FEATURE_FLAGS.WEB_APP_PHONE_NUMBER_UI_REVAMP,
  );

  const {
    phoneNumber,
    setPhoneNumber,
    setSendOTPFailedError,
    isLoadingOTP,
    sendOTPFailedError,
    agreePolicy,
    onAgreePolicy,
    handleSubmitGetOTP,
  } = useMainContext();

  const { defaultCountry, isLoadingVenueCountry } = useVenueCountryContext();

  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState<boolean | null>(
    null,
  );

  const { recordBtnClicked } = useEventTrackingContext();

  const { isNavigating: isNavigatingToBadStatusStationPage } =
    useNavigateInAppWithDelay();

  const submit = () => {
    if (!isValidPhoneNumber) return;
    recordBtnClicked(TRACKING_BUTTON.SEND_VERIFICATION_CODE);
    handleSubmitGetOTP();
  };

  useUpdateEffect(() => {
    setSendOTPFailedError(null);
  }, [phoneNumber]);

  return (
    <>
      <Stack display="grid" gap={1}>
        <LocalizationTypography
          variant="Poppins/Heading-3"
          textId="app.rentalPage.stepOne.enterPhoneNo"
          defaultText="Enter your phone number"
        />
        <LocalizationTypography
          variant="Inter/Body-sm"
          color="#6B7280"
          textId="app.rentalPage.stepOne.verifyNumber"
          defaultText="We’ll text you a code to verify your number."
        />
      </Stack>

      <Stack display="grid" mb={2} gap={3}>
        <Stack>
          {isEnabledPhoneNumberRevamp ? (
            <PhoneField
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              isValid={isValidPhoneNumber}
              setIsValid={setIsValidPhoneNumber}
              defaultCountry={defaultCountry}
              disabled={isLoadingVenueCountry}
            />
          ) : (
            <PhoneInput
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              isValid={isValidPhoneNumber}
              setIsValid={setIsValidPhoneNumber}
              defaultCountry={defaultCountry}
              disabled={isLoadingVenueCountry}
            />
          )}

          {sendOTPFailedError && (
            <Alert severity="error" sx={{ p: 0 }}>
              <LocalizationTypography
                variant="Inter/Body-sm"
                color="error"
                textId={sendOTPFailedError}
                defaultText="Failed to send code. Please try again."
              />
            </Alert>
          )}
        </Stack>

        <FormControlLabel
          sx={{ alignItems: 'flex-start', marginRight: 0 }}
          control={
            <Checkbox
              sx={{ paddingTop: 0.5 }}
              checked={agreePolicy}
              onChange={onAgreePolicy}
              size="small"
            />
          }
          label={
            <LocalizationTypography
              variant="Inter/Body-xs"
              color="#6B7280"
              textId="app.rentalPage.stepOne.submissionMessage"
              components={{
                tag1: (
                  <Typography
                    variant="Inter/Subheading-xs"
                    target="_blank"
                    color="brand.dark"
                    component="a"
                    href="https://www.chargefuze.com/terms-of-service"
                    sx={{
                      textDecoration: 'unset',
                    }}
                  />
                ),
                tag2: (
                  <Typography
                    variant="Inter/Subheading-xs"
                    target="_blank"
                    component="a"
                    color="brand.dark"
                    href="https://www.chargefuze.com/privacy-policy"
                    sx={{
                      textDecoration: 'unset',
                    }}
                  />
                ),
              }}
              defaultText="By submitting your phone number, you confirm that you have read and agreed to chargeFUZE's <tag1>Terms of Service</tag1> and <tag2>Privacy Policy</tag2>, and you consent to receive text messages from chargeFUZE for direct marketing purposes. Read our Privacy Policy to learn more about our data practices and opting-out of text communications. Msg & data rates may apply."
            />
          }
        />
      </Stack>

      <MainButton
        disabled={
          !isValidPhoneNumber ||
          isLoadingOTP ||
          !agreePolicy ||
          isNavigatingToBadStatusStationPage
        }
        variant="contained"
        onClick={submit}
        isLoading={isLoadingOTP || isNavigatingToBadStatusStationPage}
      >
        <LocalizationTypography
          variant="Poppins/Subheading-md"
          defaultText="Send code"
          textId="app.rentalPage.stepOne.buttonSendCode"
        />
      </MainButton>
    </>
  );
};
